import { ClickAwayListener } from "@mui/base";
import { Box, InputAdornment, InputBase, Popover } from "@mui/material";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { useMuiForm } from "@sinch/hooks/useMuiForm";
import { MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import { getWeekdayName } from "@sinch/utils/dateTime/format";
import { propEq } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { FormControlWrapper, MdsIcon } from "@sinch/components";
import {
  ConditionSelectProps
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionInput";
import { useConditionValuePopover } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/ConditionInput/ConditionValue/useConditionValuePopover";
import { intervalTypeTimeSchema } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import {
  TimeIntervalSettings,
  TimeIntervalTypeFormFields,
} from "@sinch/components/application/TimeIntervalSettings/TimeIntervalSettings";
import { PopoverProvider } from "@sinch/components/providers/PopoverProvider";

export const ConditionValueInputWeekdayTimes = ({
  onChange,
  value,
  onOpenChange,
  onBlur,
  readOnly,
}: ConditionSelectProps<TimeIntervalTypeFormFields>) => {
  const { safeLanguageCode } = useSettings();
  const externalValues = useMemo(
    () => ({
      daysOfWeek: intervalTypeTimeSchema.getDefault().daysOfWeek.map((item) => {
        const val = value?.daysOfWeek?.find((v) => item.weekDay === v.weekDay);
        return isNotNil(val) ? val : item;
      }),
    }),
    [value]
  );

  const form = useMuiForm<TimeIntervalTypeFormFields>({
    schema: intervalTypeTimeSchema,
    formOptions: {
      values: externalValues,
      resetOptions: { keepDefaultValues: true },
      defaultValues: externalValues,
    },
  });
  const { open, anchorEl, setAnchorEl, popoverRef, paperRef, handleToggleOpen, handleClickAway } =
    useConditionValuePopover(onBlur);

  React.useEffect(() => {
    const subscription = form.watch((formData) => {
      try {
        onChange(intervalTypeTimeSchema.validateSync(formData));
      } catch (error) {
        console.error(error);
        form.trigger();
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, form.handleSubmit, form, onChange]);

  return (
    <Box sx={{ position: "relative", width: "100%", containerType: "inline-size" }}>
      <div ref={setAnchorEl}>
        <FormControlWrapper>
          <InputBase
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  cursor: "pointer",
                  marginLeft: 0,
                  marginRight: -1,
                }}
              >
                <MdsIcon fontSize="small" icon={open ? MdsKeyboardArrowUp : MdsKeyboardArrowDown} />
              </InputAdornment>
            }
            fullWidth
            inputProps={{
              "data-cy": "ConditionInput-valueInput-string",
              readOnly: true,
            }}
            onBlur={(e) => {
              onOpenChange(false);
              onBlur?.(e);
            }}
            onClick={(e) => {
              if (readOnly) {
                return;
              }
              handleToggleOpen(e);
            }}
            onFocus={() => {
              if (readOnly) {
                return;
              }
              onOpenChange(true);
            }}
            readOnly
            sx={[
              { px: 1 },
              value === null
                ? {
                    backgroundColor: "transparent",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: ({ palette }) => palette.grey[500],
                    },
                  }
                : {},
            ]}
            value={value?.daysOfWeek
              ?.filter(propEq("enabled", true))
              .map(({ weekDay }) => getWeekdayName(weekDay, safeLanguageCode, "short"))
              .join(", ")}
          />
        </FormControlWrapper>
      </div>
      <PopoverProvider>
        <Popover
          action={popoverRef}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          disablePortal={false}
          open={open}
          slotProps={{
            paper: {
              ref: paperRef,
              elevation: 1,
              sx: {
                minWidth: "250px",
                maxWidth: "min(650px, 100vh)",
                overflow: "auto",
                zIndex: 1,
                paddingBottom: 2,
              },
            },
          }}
          sx={{ minWidth: "min(650px, 100vh)", width: "100%", zIndex: 1000 }}
        >
          <FormProvider {...form}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Box sx={{ p: 2 }}>
                <TimeIntervalSettings />
              </Box>
            </ClickAwayListener>
          </FormProvider>
        </Popover>
      </PopoverProvider>
    </Box>
  );
};
